/**
 * Who is allowed to hold an intro call (LG-902).
 *
 * The audition booking page is public: anyone who finds
 * `cal.com/<host>/audition` can pick a slot, whether or not they ever applied.
 * Prefilling and locking the email field narrows that to people holding a
 * link we sent, but nothing in cal.com can express "only this applicant, and
 * only while they are still in the running". This gate is that rule, run
 * against every `BOOKING_REQUESTED` while the event type requires
 * confirmation.
 *
 * It invents no policy. `acceptsCallBooking` in `state-machine.ts` already
 * says which stages admit a call, and that file records that a rejected
 * applicant booking calls anyway is a bug this codebase has already had. The
 * gate is the enforcement arm of a rule the funnel had no way to apply,
 * because until now the booking arrived from outside it.
 *
 * Pure, like `calcom-booking.ts`: the caller supplies the facts, so the
 * decision table is testable without a database or an HTTP client.
 */
import type { CallBookingStatus } from '../../db/schema/enums.js'
import {
  AUDITION_INTAKE_CLOSED,
  canFulfillExistingApplication,
  INTAKE_CLOSED_MESSAGE,
} from './intake.js'
import { type AuditionStage, acceptsCallBooking } from './state-machine.js'

export type GateAction = 'confirm' | 'decline'

export interface BookingGate {
  action: GateAction
  /** Internal, for logs and the admin surface. Never shown to the applicant. */
  reason: string
  /**
   * What cal.com emails the applicant when we decline.
   *
   * Deliberately uniform and uninformative. A decline reason is delivered by
   * a calendar robot with no context and no reply path, which is the worst
   * possible way to learn you have been rejected — so it never says. The real
   * reason lives in {@link BookingGate.reason}, where a human can see it
   * before choosing how to tell them.
   */
  message?: string
}

/**
 * Marks a `gateReason` recording that a booking never faced the gate at all:
 * it arrived already confirmed, which happens only when the event type's
 * "requires confirmation" toggle has been turned off and this whole module is
 * therefore inert.
 *
 * Declared here and imported by both the recorder that writes it and the
 * admin page that renders it, for the same reason `CALCOM_SUBMISSION_FIELD`
 * is: a prefix spelled out twice is a prefix that eventually differs, and the
 * symptom would be the warning silently never showing again.
 */
export const UNGATED_REASON_PREFIX = 'ungated:'

/**
 * Does this `gateReason` say the gate never ran?
 *
 * Unlike every other reason, this one belongs on a booking of ANY status —
 * an ungated booking is `booked`, not `declined` — which is why callers ask
 * this rather than filtering on status.
 */
export function isUngatedReason(reason: string | null | undefined): boolean {
  return reason?.startsWith(UNGATED_REASON_PREFIX) ?? false
}

/**
 * What we tell an applicant whose stage refuses a call — today, someone
 * still mid-application or someone already rejected. One sentence for both
 * on purpose: the refusal renders as the reason line inside cal.com's
 * stock rejection email, so any difference between the two would be the
 * tell. It apologises, holds no slot, and hands them a route to a human;
 * it does not mention links or accounts, which read as a system fault.
 */
const NEUTRAL_DECLINE =
  "Sorry, we couldn't hold this slot for you. If you were expecting to be " +
  'able to book a call, reply to the most recent email you had from us and ' +
  'a real person will get back to you.'

/**
 * What we tell someone whose booking matches no application.
 *
 * Unlike {@link NEUTRAL_DECLINE} this one is specific, because it can afford
 * to be: it reveals nothing about anybody's application — there isn't one —
 * and the person reading it needs the route back in. `NEUTRAL_DECLINE`'s
 * "reply to the most recent email you had from us" is actively wrong here,
 * sending someone who has never heard from us to search an empty inbox.
 */
function noApplicationMessage(applyUrl: string): string {
  if (AUDITION_INTAKE_CLOSED) return INTAKE_CLOSED_MESSAGE
  const where = applyUrl ? ` at ${applyUrl}` : ''
  return (
    'We could not find an application for this email address. Intro calls ' +
    `are booked from a link we send once you have applied — apply${where} ` +
    'and we will send you one.'
  )
}

/**
 * Which booking statuses count as a call the applicant currently holds.
 *
 * Exhaustive on purpose. The recorder used to spell this as
 * `status === 'pending' || status === 'booked'`, so a status added to
 * `callBookingStatusEnum` broke the queue's `Record` at compile time and
 * silently read as "not a call" here — the fail-open direction, where the
 * gate lets a second booking through. Now a new status is a compile error
 * until someone says which side of the line it falls on.
 */
const ACTIVE_BOOKING_STATUS: Record<CallBookingStatus, boolean> = {
  // The slot is held while we decide; a second request on top of it is still
  // a second call.
  pending: true,
  booked: true,
  // Never became a call.
  declined: false,
  cancelled: false,
  // Already took place.
  held: false,
  no_show: false,
}

/** Does a booking in this status count as a call the applicant holds? */
export function isActiveBookingStatus(status: CallBookingStatus): boolean {
  return ACTIVE_BOOKING_STATUS[status]
}

export interface GateInput {
  /** The applicant's funnel stage, or null when nothing matched the booking. */
  stage: AuditionStage | null
  stripePaymentIntentId: string | null
  /**
   * Live bookings this applicant already holds, NOT counting the one being
   * decided and not counting the one it reschedules. "Live" is
   * {@link isActiveBookingStatus}, declared beside this so the rule and its
   * implementation cannot drift apart.
   */
  otherActiveBookings: number
  /** True when this request replaces a booking we already granted them. */
  isReschedule: boolean
  /**
   * Where to send someone who has not applied, or '' when no public base URL
   * is configured. Passed in rather than read from the environment so this
   * module stays a pure decision table.
   */
  applyUrl?: string
}

/**
 * What to do with a pending booking request.
 *
 * Order matters, and the first two rules are the ones that stop this being a
 * blunt instrument:
 *
 * - An unmatched booking is **declined, and told how to come back** (LG-940).
 *   This used to be held for a human, on the theory that refusing might bounce
 *   a real applicant who booked from an address we do not know. The cal.com
 *   event type rules that out: our link prefills the email with
 *   `disableOnPrefill`, so an applicant coming through it *cannot* book under
 *   another address, and `requiresBookerEmailVerification` means whoever did
 *   book proved they own the inbox the refusal lands in. What is left is
 *   someone who never applied — and for them the decline is not a dead end but
 *   the instruction, because applying is self-service.
 * - A reschedule is confirmed outright, before the booking count or the
 *   stage is consulted: they are moving a call we already said yes to, so
 *   neither has anything to add. The order also decides what the row says —
 *   an admin should read "reschedule", not "rebooking after cancelling".
 */
export function bookingGate({
  stage,
  stripePaymentIntentId,
  otherActiveBookings,
  isReschedule,
  applyUrl = '',
}: GateInput): BookingGate {
  if (stage === null) {
    return {
      action: 'decline',
      reason: 'booking matched no application',
      message: noApplicationMessage(applyUrl),
    }
  }

  if (isReschedule) {
    return { action: 'confirm', reason: 'reschedule of a call already granted' }
  }

  if (
    AUDITION_INTAKE_CLOSED &&
    !canFulfillExistingApplication({ stage, stripePaymentIntentId })
  ) {
    return {
      action: 'decline',
      reason: 'new application bookings are closed',
      message: INTAKE_CLOSED_MESSAGE,
    }
  }

  if (otherActiveBookings > 0) {
    return {
      action: 'decline',
      reason: 'applicant already holds an active booking',
      message:
        'You already have an intro call booked with us. Use the link in your ' +
        'confirmation email to move it rather than booking a second one.',
    }
  }

  if (acceptsCallBooking(stage)) {
    return {
      action: 'confirm',
      // Two reasons for one answer because an admin reads them off the row:
      // a booking from `vibe_call_booked` means their previous call was
      // cancelled (that is the only way to get here with none active), and
      // "stage vibe_call_booked accepts a call" would hide it.
      reason:
        stage === 'vibe_call_booked'
          ? 'rebooking after cancelling their previous call'
          : `stage ${stage} accepts a call`,
    }
  }

  return {
    action: 'decline',
    reason: `stage ${stage} does not accept a call`,
    message: NEUTRAL_DECLINE,
  }
}
